import Vue from 'vue';
import get from 'lodash/get';
import cloneObject from '@/helpers/cloneObject';
import uniqueId from '@/helpers/uniqueId';
import { pushDataLayer } from '@/helpers/gtm';
import PageValidator from '@/validation/PageValidator';
import presets from '@/store/dicts/presets';
import api from '@/store/helpers/api';
import isPresetEnabled from '@/store/helpers/isPresetEnabled';
import isEqual from 'lodash/isEqual';

export default {
  state: {
    // TODO: Set single activeContainerId instead of each type's
    // Do it later so there's not much refact outside store
    activePageId: null,
    activeModalId: null,
    activeGlobalInstanceId: null,
    instances: {},
    instancesThis: {},
    activeInstance: null,
    activeInstanceExpandedGroup: null,
    showPresetsContent: false,
    presetIndex: null,
    savedOn: new Date(),
    validationMessages: {},
    validationMessagesCount: 0,
    showValidationPanel: false,
    savedInstances: {},
    currentEditableItem: null,
  },
  mutations: {
    // TODO: Set single activeContainerId instead of each type's
    // Do it later so there's not much refact outside store
    SET_ACTIVE_PAGE_ID(state, id) {
      state.activePageId = id;
    },
    SET_ACTIVE_MODAL_ID(state, id) {
      state.activeModalId = id;
    },
    SET_ACTIVE_GLOBAL_INSTANCE_ID(state, id) {
      state.activeGlobalInstanceId = id;
    },
    SET_INSTANCE({ instances }, instance) {
      let instanceToAdd = instance;

      if (instances[instance.id]) {
        instanceToAdd = { ...instances[instance.id], ...instance };
      }

      Vue.set(instances, instance.id, instanceToAdd);
    },
    SET_SAVED_INSTANCE({ savedInstances }, instance) {
      let instanceToAdd = instance;

      if (savedInstances[instance.id]) {
        instanceToAdd = { ...savedInstances[instance.id], ...instance };
      }

      Vue.set(savedInstances, instance.id, instanceToAdd);
    },
    ADD_INSTANCE_THIS(state, payload) {
      Vue.set(state.instancesThis, payload.instanceId, payload);
    },
    REMOVE_INSTANCE_THIS(state, payload) {
      Vue.delete(state.instancesThis, payload);
    },
    ADD_INSTANCE_TO_CONTAINER(state, { path, instanceId, index }) {
      const container = get(state, path);

      if (typeof index === 'number') {
        container.instanceList.splice(index, 0, instanceId);
      } else {
        container.instanceList.push(instanceId);
      }
    },
    SET_INSTANCE_INDEX(state, { page, index, instanceId }) {
      // TODO: Mutate state instead of payload getter
      // Refact later cause it only affect pages
      Vue.set(page.instanceList, index, instanceId);
    },
    REMOVE_INSTANCE(state, instanceId) {
      Vue.delete(state.instances, instanceId);
    },
    REMOVE_INSTANCE_FROM_CONTAINER(state, { path, instanceId }) {
      const container = get(state, path);
      const index = container.instanceList.indexOf(instanceId);

      if (index > -1) {
        container.instanceList.splice(index, 1);
      }
    },
    SET_ACTIVE_INSTANCE(state, instanceId) {
      state.activeInstance = instanceId;
    },
    SET_ACTIVE_INSTANCE_THIS(state, instance) {
      state.activeInstanceThis = instance;
    },
    SET_ACTIVE_EDITABLE_OBJECT_DATA(state, { path, value }) {
      let target = state;
      if (state.activeGlobalData) {
        target = state.globalData[state.activeGlobalData];
      } else if (state.activeInstance) {
        target = state.instances[state.activeInstance];
      }

      let key = path;

      /**
       * Path can be a ordinary property name or use dot notation like
       * "repeatableGroup.0.someProperty". We used to use Lodash's set because
       * of the latter case, but it turned out that it doesn't trigger Vue
       * reactivity when adding new properties to existent objects.
       *
       * In order to use Vue.set, we split the path and use last piece as key.
       * The remaining ones are added to target. Some examples:
       *
       * path = "someProperty"
       * target = state.instances.someInstanceKey.data
       * key = "someProperty"
       *
       * path = "repeatableGroup.0.someProperty"
       * target = state.instances.someInstanceKey.data.repeatableGroup[0]
       * key = "someProperty"
       */
      if (key.includes('.')) {
        const keyPieces = key.split('.');

        key = keyPieces.pop(keyPieces);
        target = get(target, keyPieces.join('.'));
      }

      // Temporary condition to avoid late multiple computed setting
      // TODO: Avoid UI type HTML to trigger set twice on key change
      if (target) {
        Vue.set(target, key, value);
      }
    },
    SET_PRESET_LIST_DISPLAY(state, showPresetsContent) {
      state.showPresetsContent = showPresetsContent;
    },
    SET_PRESET_INDEX(state, presetIndex) {
      state.presetIndex = presetIndex;
    },
    UPDATE_SAVE_TIME(state) {
      state.savedOn = new Date();
    },
    SET_VALIDATION_MESSAGES(state, validationMessages) {
      state.validationMessages = validationMessages;
    },
    SET_VALIDATION_MESSAGES_COUNT(state, validationMessagesCount) {
      state.validationMessagesCount = validationMessagesCount;
    },
    SET_VALIDATION_PANEL_DISPLAY(state, showValidationPanel) {
      state.showValidationPanel = showValidationPanel;
    },
    SET_ACTIVE_INSTANCE_EXPANDED_GROUP(state, activeInstanceExpandedGroup) {
      state.activeInstanceExpandedGroup = activeInstanceExpandedGroup;
    },
    UPDATE_SAVED_INSTANCES(state, instances) {
      // These instances should not be reactive
      state.savedInstances = cloneObject(instances);
    },
    SET_CURRENT_EDITABLE_ITEM(state, itemPath) {
      state.currentEditableItem = itemPath;
    },
  },
  getters: {
    containerId: ({ activePageId, activeModalId, activeGlobalInstanceId }) =>
      activePageId ?? activeModalId ?? activeGlobalInstanceId,
    // TODO: Eliminate single type's active container getters
    // Do it later so there's not much refact outside store
    activePage: ({ pages, activePageId }) =>
      pages[activePageId] && { ...pages[activePageId], type: 'pages' },
    activeModal: ({ globalInstances, activeModalId }) =>
      globalInstances[activeModalId] && {
        ...globalInstances[activeModalId],
        type: 'modals',
      },
    activeGlobalInstance: ({ globalInstances, activeGlobalInstanceId }) =>
      globalInstances[activeGlobalInstanceId] && {
        ...globalInstances[activeGlobalInstanceId],
        type: 'globalInstances',
      },
    container: (state, { activePage, activeModal, activeGlobalInstance }) =>
      activePage ??
      activeModal ??
      activeGlobalInstance ?? { id: 'settings', language: '' },
    // TODO: Refact to use instance getter to global instances, global data and modals
    // Instead of setting it as a new instance in the instances state
    activeExternalGlobalInstance: (
      { globalInstances, activeInstance },
      { container },
    ) => container.type === 'pages' && globalInstances[activeInstance],
    instance: (state) => (instanceId) => state.instances[instanceId],
    // eslint-disable-next-line arrow-body-style
    instanceIndex:
      (state, { container }) =>
      (instanceId) =>
        container.instanceList?.indexOf(instanceId),
    instanceCount: (state, { container }) => container.instanceList?.length,
    isFirstInstance: (state, getters) => (instanceId) =>
      getters.instanceIndex(instanceId) === 0,
    // eslint-disable-next-line arrow-body-style
    isLastInstance: (state, getters) => (instanceId) => {
      return getters.instanceIndex(instanceId) === getters.instanceCount - 1;
    },
    activeInstance: (state) => state.instances[state.activeInstance],
    activeInstanceThis: (state) => state.instancesThis[state.activeInstance],
    activeEditableObjectData:
      (state, { activeGlobalData, activeInstance }) =>
      ({ path }) => {
        const target = activeGlobalData || activeInstance || state;

        return get(target, path);
      },
    settingsInstanceId: (state, { container }) => container.settingsInstance,
    containerSettings: (state, getters) => {
      const { settingsInstanceId } = getters;
      return settingsInstanceId && state.instances[settingsInstanceId].data;
    },
  },
  actions: {
    storeInstance({ commit }, instance) {
      // TODO: Remove prop defaults as initial data so it doesn't save
      // instanceId or other not data related props
      const instanceToAdd = cloneObject(instance);
      delete instanceToAdd.data.instanceId;

      // TODO: Review if it's actually better to save instane's id within its object
      commit('SET_INSTANCE', instanceToAdd);
      // cannot be the same object as above
      commit('SET_SAVED_INSTANCE', cloneObject(instanceToAdd));
    },
    addPresetToActiveContainer(
      { dispatch, state: { presetIndex: index } },
      preset,
    ) {
      const {
        category,
        keywords,
        unique,
        isNew,
        isUpdated,
        createdAt,
        updatedAt,
        ...instanceData
      } = preset; // removes unused keys from data before saving

      dispatch('addInstanceToActiveContainer', {
        instance: instanceData,
        index,
      });

      pushDataLayer({ event: 'module-added', module: preset.name });
    },
    addInstanceToActiveContainer(
      {
        commit,
        getters: {
          container: { id, type },
        },
        dispatch,
      },
      { instance: baseInstance, index },
    ) {
      const instance = { ...baseInstance, id: uniqueId() };

      if (typeof instance.data === 'undefined') {
        instance.data = {};
      }

      delete instance.image;

      commit('SET_INSTANCE', cloneObject(instance));

      commit('ADD_INSTANCE_TO_CONTAINER', {
        instanceId: instance.id,
        path: `${type}.${id}`,
        index,
      });

      if (index) {
        commit('SET_PRESET_LIST_DISPLAY', false);
        commit('SET_PRESET_INDEX', null);
      }

      dispatch('validateActiveContainer');
    },
    // TODO: Refact later cause it only affect pages
    moveInstanceUp({ commit, getters }, instanceId) {
      if (!getters.isFirstInstance(instanceId) > 0) {
        const index = getters.instanceIndex(instanceId);
        const previousId = getters.activePage.instanceList[index - 1];

        commit('SET_INSTANCE_INDEX', {
          page: getters.activePage,
          instanceId,
          index: index - 1,
        });

        commit('SET_INSTANCE_INDEX', {
          page: getters.activePage,
          instanceId: previousId,
          index,
        });
      }
    },
    // TODO: Refact later cause it only affect pages
    moveInstanceDown({ commit, getters }, instanceId) {
      if (!getters.isLastInstance(instanceId) > 0) {
        const index = getters.instanceIndex(instanceId);
        const nextId = getters.activePage.instanceList[index + 1];

        commit('SET_INSTANCE_INDEX', {
          page: getters.activePage,
          instanceId,
          index: index + 1,
        });

        commit('SET_INSTANCE_INDEX', {
          page: getters.activePage,
          instanceId: nextId,
          index,
        });
      }
    },
    cloneInstance({ getters, dispatch }, instanceId) {
      const index = getters.instanceIndex(instanceId);

      if (index > -1) {
        const baseInstance = getters.instance(instanceId);
        const instance = cloneObject(baseInstance);

        dispatch('addInstanceToActiveContainer', {
          instance,
          index: index + 1,
        });
      }
    },
    // TODO: Refact later cause it only affect pages
    cloneInstanceToAnotherPage({ commit, state, getters }, pageData) {
      return new Promise((resolve, reject) => {
        const index = getters.instanceIndex(pageData.instanceId);

        if (index > -1) {
          const baseInstance = getters.instance(pageData.instanceId);
          const instance = cloneObject(baseInstance);

          api.get(`pages/${pageData.urlPath}`).then((response) => {
            const instanceId = uniqueId();

            if (typeof instance.data === 'undefined') {
              instance.data = {};
            }

            delete instance.image;

            /** Página de destino */
            const page = response.data;
            /** Instância que será copiada */
            const newInstance = getters.instance(pageData.instanceId);

            /** Preset que não está desativado com base no isEnabled */
            const enabledPreset = presets.find(
              (presetItem) =>
                presetItem.module === newInstance.module &&
                isPresetEnabled(presetItem, state, getters),
            );

            /** A instância existe na página de destino */
            const existInstance = Object.values(page.instances).some(
              (instanceItem) => {
                if (
                  newInstance.module.includes('Widget') &&
                  instanceItem.module.includes('Widget')
                ) {
                  return true;
                }

                return instanceItem.module === newInstance.module;
              },
            );

            if (enabledPreset && (!existInstance || !enabledPreset.unique)) {
              page.instanceList.push(instanceId);
              page.instances[instanceId] = newInstance;

              api
                .patch(`pages/${pageData.urlPath}`, page)
                .then(() => {
                  commit('UPDATE_SAVE_TIME');
                  commit('SET_PAGE_DIRTINESS', {
                    pageId: page.id,
                    isDirty: true,
                  });
                })
                .then(resolve)
                .catch((error) => console.log(error))
                .finally(() => commit('SET_LOADING_STATE', false));
            } else {
              reject();
            }
          });
        }
      });
    },
    removeInstance(
      {
        commit,
        state,
        getters: {
          container: { type, id },
          instanceIndex,
        },
        dispatch,
      },
      instanceId,
    ) {
      const index = instanceIndex(instanceId);

      if (index > -1) {
        commit('SET_VALIDATION_MESSAGES', {});
        commit('SET_VALIDATION_MESSAGES_COUNT', 0);
        commit('REMOVE_INSTANCE', instanceId);
        commit('REMOVE_INSTANCE_FROM_CONTAINER', {
          path: `${type}.${id}`,
          instanceId,
        });

        if (state.activeInstance === instanceId) {
          commit('SET_ACTIVE_INSTANCE', null);
        }

        dispatch('validateActiveContainer');
      }
    },
    toggleActiveInstance({ commit, state }, instanceId) {
      if (state.activeInstance === instanceId) {
        commit('SET_ACTIVE_INSTANCE', null);
      } else {
        commit('SET_ACTIVE_INSTANCE', instanceId);
        commit('SET_PRESET_LIST_DISPLAY', false);
        commit('SET_VALIDATION_PANEL_DISPLAY', false);
      }
    },
    togglePresetListDisplay(
      { commit, state: { showPresetsContent }, getters: { instanceIndex } },
      { instanceId, position } = {},
    ) {
      commit('SET_PRESET_LIST_DISPLAY', !showPresetsContent);

      if (instanceId) {
        commit(
          'SET_PRESET_INDEX',
          position === 'top'
            ? instanceIndex(instanceId)
            : instanceIndex(instanceId) + 1,
        );
      }

      if (!showPresetsContent) {
        commit('SET_ACTIVE_INSTANCE', null);
        commit('SET_VALIDATION_PANEL_DISPLAY', false);
      }
    },
    // Moved to editor store only actions directly related to the active editor container
    setActiveContainerDirty({
      commit,
      getters: {
        container: { id, type },
      },
    }) {
      const { mutation, idParam } = {
        pages: {
          mutation: 'SET_PAGE_DIRTINESS',
          idParam: 'pageId',
        },
        modals: {
          mutation: 'SET_GLOBAL_INSTANCE_DIRTINESS',
          idParam: 'globalId',
        },
        globalInstances: {
          mutation: 'SET_GLOBAL_INSTANCE_DIRTINESS',
          idParam: 'globalId',
        },
      }[type]; // Get mutation to use and param to send based on container's type
      // TODO: Refact mutations params to avoid always have to consider it

      commit(mutation, {
        [idParam]: id,
        isDirty: true,
      });
    },
    publishActiveContainer({ getters: { container }, dispatch }) {
      if (container) {
        const { action, containerParam, endpoint } = {
          pages: {
            action: 'setPageStatus',
            containerParam: 'page',
            endpoint: 'pages',
          },
          modals: {
            action: 'setGlobalInstanceStatus',
            containerParam: 'global',
            endpoint: 'modals',
          },
          globalInstances: {
            action: 'setGlobalInstanceStatus',
            containerParam: 'global',
            endpoint: 'globals',
          },
        }[container.type]; // Get action to use and param to send based on container's type
        // TODO: Refact actions params to avoid always have to consider it
        // and send unused data, like the whole active container object

        dispatch(action, {
          [containerParam]: container,
          isPublished: true,
          endpoint,
        });
      }
    },
    saveActiveContainer(
      {
        commit,
        state: { instances, savedInstances, globalInstances },
        getters: {
          container: { type, created, ...baseContainer },
        },
        dispatch,
      },
      bypassCheck = false,
    ) {
      /**
       * The flag bypassCheck allows to force a patch even if the instances didn't change
       * Ex: when changing instances position
       */
      if (
        baseContainer &&
        (bypassCheck ||
          !isEqual(cloneObject(instances), cloneObject(savedInstances)))
      ) {
        // TODO: Refact to save instances only after success patch request
        commit('UPDATE_SAVED_INSTANCES', instances);

        // JSON stringify/parse clones the modal to prevent it being passed as reference
        let container = cloneObject(baseContainer);

        commit('SET_LOADING_STATE', true);

        if (type === 'pages') {
          container.instances = {
            [baseContainer.settingsInstance]:
              instances[baseContainer.settingsInstance],
          };

          baseContainer.instanceList.forEach((instanceId) => {
            // Only insert it in container instances if it's not global
            // The global instance id must remain in container instance list though
            if (!globalInstances[instanceId]) {
              container.instances[instanceId] = cloneObject(
                instances[instanceId],
              );
            }
          });
        } else {
          container = { ...container, ...instances[container.id] };
        }

        const apiPath =
          type === 'pages'
            ? `${container.language}/${container.urlPath}`
            : container.id;

        // TODO: Set globalInstances state as globals to
        // reuse its type on state calls as well on api calls
        const apiType = type === 'globalInstances' ? 'globals' : type;

        // TODO: Fix double post request on create
        // TODO: Consider this for all types when pages have a created property
        // So we can have fixed main to any kind of container
        const apiCall =
          type === 'globalInstances' && !created
            ? api.post(apiType, container)
            : api.patch(`${apiType}/${apiPath}`, container);

        apiCall
          .then(() => {
            commit('UPDATE_SAVE_TIME');
            if (type === 'globalInstances' && !created) {
              dispatch('storeGlobal', {
                created: true,
                endpoint: apiType,
                ...container,
              });
            }

            dispatch('setActiveContainerDirty');
            dispatch('validateActiveContainer');
          })
          .catch((error) => console.log(error))
          .finally(() => commit('SET_LOADING_STATE', false));
      }
    },
    validateActiveContainer({ commit, getters: { container } }) {
      if (container) {
        const validator = new PageValidator(container, this);

        validator.validate();

        commit('SET_VALIDATION_MESSAGES', validator.getMessages());
        commit('SET_VALIDATION_MESSAGES_COUNT', validator.getMessagesCount());
      }
    },
    toggleValidationPanelDisplay({ commit, state }, showValidationPanel) {
      let validationPanelDisplay = showValidationPanel;

      if (typeof validationPanelDisplay !== 'boolean') {
        validationPanelDisplay = !state.showValidationPanel;
      }

      commit('SET_VALIDATION_PANEL_DISPLAY', validationPanelDisplay);

      if (validationPanelDisplay) {
        commit('SET_PRESET_LIST_DISPLAY', false);
        commit('SET_ACTIVE_INSTANCE', null);
      }
    },
    setActiveInstanceExpandedGroup({ commit }, activeInstanceExpandedGroup) {
      commit('SET_ACTIVE_INSTANCE_EXPANDED_GROUP', activeInstanceExpandedGroup);
    },
    setCurrentEditableItem({ commit }, itemPath) {
      commit('SET_CURRENT_EDITABLE_ITEM', itemPath);
    },
  },
};
